import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../utils/factoryUtils";
import { EscenaItem, SateliteService } from "../../../generated/grpc/satelite_pb";
import { grpcSateliteDatabase } from "../database/GrpcSateliteDatabase";
import { serviceSync } from "../../../base/services/ServiceSync";
import { EscenaResponse } from "../response/EscenaResponse";

/** Item guardado localmente: `EscenaItem` (gRPC) + `fincaId` inyectado acá
 *  -no viaja en el mensaje (el cliente ya sabe qué finca pidió, ver
 *  `fertilizacion.proto`/`terceros_lote.proto` para el mismo criterio)-, pero
 *  hace falta como propiedad real del registro para el índice `fincaId` de
 *  `GrpcSateliteDatabase`. */
type StoredEscenaItem = EscenaItem & { fincaId: string };

/** Variante gRPC-Web (con delta real, por finca) de `EscenaGetListByFincaIdService`
 *  (ver ese archivo). Devuelve `EscenaResponse[]` -mismo shape que ya usa el
 *  resto de la app-. Rellena con valores inertes solo los campos que sí se
 *  comprobó que nadie lee (`fincaId`, `sceneId`, `viewId` — ver el comentario
 *  de `EscenaItem` en el proto). `cloudsPercentage`/`shadowsPercentage` SÍ se
 *  mapean del real: `SateliteBottomSheetRender` los usa para filtrar qué
 *  escenas son mostrables, y forzarlos a null acá hacía que ESA escena nunca
 *  pasara el filtro -toda finca con datos terminaba mostrando "sin datos"-. */
class EscenaGrpcService {
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<EscenaResponse[]> {
    await this._syncAsync();
    const items = await grpcSateliteDatabase.getEscenasByFincaIdAsync<StoredEscenaItem>(
      this._fincaId,
    );
    return items.map(toEscenaResponse);
  }

  clearCache(): void {
    serviceSync.reset(`grpc:escenas:${this._fincaId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:escenas:${this._fincaId}`, async () => {
      const ultimaSincronizacion = await grpcSateliteDatabase.getUltimaSincronizacionEscenasAsync(
        this._fincaId,
      );
      const client = createClient(SateliteService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listEscenasByFincaId({
          fincaId: this._fincaId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      const items: StoredEscenaItem[] = response.items.map((item) => ({
        ...item,
        fincaId: this._fincaId,
      }));
      await grpcSateliteDatabase.putEscenasDeltaAsync(
        this._fincaId,
        items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por fincaId (comparte cooldown/caché,
 *  mismo criterio que `fx_createEscenaGetListByFincaIdService`). */
export const fx_createEscenaGrpcService = createCachedFactory(
  (fincaId: string) => new EscenaGrpcService(fincaId),
);

function toEscenaResponse(escena: EscenaItem): EscenaResponse {
  return {
    id: escena.id,
    isEliminado: escena.eliminado,
    fincaId: "",
    sceneId: "",
    viewId: "",
    fecha: escena.fecha,
    cloudsPercentage: escena.cloudsPercentage ?? null,
    shadowsPercentage: escena.shadowsPercentage ?? null,
    totalUsefulArea: escena.totalUsefulArea ?? null,
    totalNotUsefulArea: escena.totalNotUsefulArea ?? null,
    indices: escena.indices.map((tile) => ({
      indiceId: tile.indiceId,
      imageUri: tile.imageUri,
    })),
  };
}
